import { SurfCloudAPI } from "../graphql/surfApi.js";
import { downloadSurfBlobText } from "./surfBlobDownload.js";
import { pollSurfImageJob } from "./surfImageJob.js";
import { pollSurfAIJob, extractAIJobOutputText } from "./surfAiJob.js";
import { applySurfJobToDeck } from "./surfJobAppliers.js";
import { parseSurfTargetFromParamsJson } from "./surfJobTarget.js";
import { createQueuedJob } from "./jobQueue.js";
const TERMINAL = new Set(["COMPLETED", "FAILED", "CANCELLED", "TIMED_OUT"]);
const TEXT_JOB_OPS = new Set(["speaker.script", "outline"]);
const TEXT_JOB_FEATURES = new Set(["surf.speaker.script", "surf.slide.extract"]);
function isTextOutputJob(job, hintOp) {
    if (hintOp && TEXT_JOB_OPS.has(hintOp))
        return true;
    const feat = job.feature?.trim();
    if (feat && TEXT_JOB_FEATURES.has(feat))
        return true;
    const surfTarget = parseSurfTargetFromParamsJson(job.paramsJson ?? undefined);
    if (surfTarget?.op && TEXT_JOB_OPS.has(surfTarget.op))
        return true;
    const mime = job.outputAssetMime?.trim().toLowerCase();
    if (mime === "application/json" || mime === "text/plain")
        return true;
    return false;
}
async function resolveTextJobOutput(cfg, job) {
    const inline = extractAIJobOutputText(job.providerUsageJson);
    if (inline)
        return inline;
    const key = job.outputAssetKey?.trim();
    const presigned = cfg.presignedBaseUrl?.trim();
    if (key && presigned) {
        return downloadSurfBlobText(presigned, () => cfg.getIdToken(), key);
    }
    return pollSurfAIJob(cfg, job.jobId, 180_000);
}
export async function resolveJobOutput(cfg, jobId, options) {
    const job = await SurfCloudAPI.getAIJob(cfg, jobId);
    if (!TERMINAL.has(job.status)) {
        return { status: job.status };
    }
    if (job.status !== "COMPLETED") {
        return { status: job.status, error: job.errorMessage ?? `Job ${job.status}` };
    }
    const textJob = isTextOutputJob(job, options?.op);
    const assetKey = job.outputAssetKey?.trim();
    if (textJob) {
        try {
            const text = await resolveTextJobOutput(cfg, { ...job, jobId });
            return { status: job.status, output: { kind: "text", text } };
        }
        catch {
            return { status: job.status, error: "Job completato senza output testo." };
        }
    }
    if (assetKey) {
        return { status: job.status, output: { kind: "asset", assetKey } };
    }
    try {
        const text = await pollSurfAIJob(cfg, jobId, 180_000);
        return { status: job.status, output: { kind: "text", text } };
    }
    catch {
        return { status: job.status, error: "Job completato senza output." };
    }
}
export function applyCompletedJobToDeck(deck, job, output) {
    return applySurfJobToDeck(deck, {
        op: job.op,
        target: job.target,
        output,
        applyMeta: job.applyMeta,
    });
}
export function queuedJobFromSubmit(submit) {
    return createQueuedJob({
        jobId: submit.jobId,
        deckId: submit.deckId,
        op: submit.op,
        target: submit.target,
        label: submit.label,
        applyMeta: submit.applyMeta,
        status: "pending",
    });
}
export function queuedJobFromServerJob(serverJob) {
    const surfTarget = parseSurfTargetFromParamsJson(serverJob.paramsJson ?? undefined);
    if (!surfTarget)
        return null;
    const status = serverJob.status === "COMPLETED"
        ? "completed"
        : serverJob.status === "FAILED" || serverJob.status === "TIMED_OUT"
            ? "failed"
            : "running";
    return createQueuedJob({
        jobId: serverJob.jobId,
        deckId: surfTarget.deckId,
        op: surfTarget.op,
        target: {
            slideId: surfTarget.slideId,
            layerId: surfTarget.layerId,
            editKind: surfTarget.editKind,
        },
        label: surfTarget.op,
        applyMeta: surfTarget.applyMeta,
        status,
    });
}
export async function pollJobAssetKey(cfg, jobId, timeoutMs = 180_000) {
    return pollSurfImageJob(cfg, jobId, timeoutMs);
}
export { TERMINAL };
